/*
 * Prime Example — gallery dots, mobile only.
 *
 * Renders a row of dots over the product gallery showing which slide is active.
 * They are an INDICATOR, not a control: the edge arrows and swipe do the
 * navigating. Dawn's own text counter stays in the DOM, visually hidden, so a
 * screen reader still hears "1 of 4"; these dots are aria-hidden decoration.
 *
 * WHY IT BINDS TO DAWN'S COUNTER INSTEAD OF COUNTING SLIDES
 *
 * The gallery holds one slide per variant image for EVERY colourway at once —
 * 36 slide elements on this product — and hides all but the selected variant's.
 * Counting DOM children gives 36 and is always wrong. Dawn already tracks the
 * real numbers in `.slider-counter--current` and `.slider-counter--total` and
 * updates them on scroll AND on variant change, so this reads those two spans
 * and mirrors them. That also means the dot count rebuilds correctly when the
 * customer switches to a colourway with a different number of photos, for free.
 *
 * A MutationObserver on the counter is the whole engine: no scroll listener, no
 * slide math, no duplicated state. When Dawn changes the numbers, the dots
 * follow.
 */

(() => {
  // One selector, not a comma list. An earlier `.product__info-container, .product`
  // prefix expanded — the comma binds loosest — to two selectors, the first of
  // which (`.product__info-container` alone) matched the info container itself
  // and constructed a stray instance. It early-returned only because that
  // element has no `.slider-counter`; a future nesting change would have
  // double-initialised and appended a second dot row. This is the only gallery.
  const GALLERY = '.product .product__media-wrapper slider-component';
  const MOBILE = '(max-width: 749px)';

  class GalleryDots {
    constructor(slider) {
      this.slider = slider;
      this.counter = slider.querySelector('.slider-counter');
      this.dots = null;
      this.observer = null;
      this.mql = window.matchMedia(MOBILE);

      this.onChange = this.onChange.bind(this);
      this.sync = this.sync.bind(this);

      // No counter means a single-image product with no slider to track.
      if (!this.counter) return;
      this.mql.addEventListener('change', this.onChange);
      this.onChange();
    }

    onChange() {
      if (this.mql.matches) this.enable();
      else this.disable();
    }

    enable() {
      if (this.dots) return;
      this.dots = document.createElement('div');
      this.dots.className = 'pe-gallery-dots';
      this.dots.setAttribute('aria-hidden', 'true');
      this.slider.appendChild(this.dots);

      // characterData + subtree because Dawn rewrites the spans' text nodes in
      // place; childList in case it replaces a span outright.
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.counter, {
        subtree: true,
        childList: true,
        characterData: true,
      });
      this.sync();
    }

    disable() {
      if (!this.dots) return;
      this.observer?.disconnect();
      this.observer = null;
      this.dots.remove();
      this.dots = null;
    }

    read() {
      const n = (sel) => parseInt(this.counter.querySelector(sel)?.textContent || '', 10);
      return { total: n('.slider-counter--total'), current: n('.slider-counter--current') };
    }

    sync() {
      if (!this.dots) return;
      const { total, current } = this.read();
      if (!Number.isFinite(total) || total < 1) {
        this.dots.hidden = true;
        return;
      }
      this.dots.hidden = false;

      // A single image needs no dots.
      if (total < 2) {
        this.dots.replaceChildren();
        return;
      }

      // Rebuild only when the count changed (variant switch); otherwise just
      // move the active class, so scrolling does not thrash the DOM.
      if (this.dots.childElementCount !== total) {
        const frag = document.createDocumentFragment();
        for (let i = 0; i < total; i++) frag.appendChild(document.createElement('i'));
        this.dots.replaceChildren(frag);
      }

      const active = Number.isFinite(current) ? current - 1 : 0;
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
      });
    }

    destroy() {
      this.disable();
      this.mql.removeEventListener('change', this.onChange);
    }
  }

  const instances = new WeakMap();

  function init(root = document) {
    root.querySelectorAll(GALLERY).forEach((slider) => {
      if (instances.has(slider)) return;
      instances.set(slider, new GalleryDots(slider));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('slider-component').forEach((slider) => {
      instances.get(slider)?.destroy();
      instances.delete(slider);
    });
  });
})();
